/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { oc } from 'ts-optchain';
import _ from 'lodash';
import clsx from 'clsx';

import BaseComponent from '../BaseComponent';
import Input from './Input';
import InputContainer from '../forms/InputContainer';
import TextInputAffix from './TextInputAffix';

export interface Props<T> {
  ariaLabel?: string;
  autoComplete?: string;
  autoCorrect?: string;
  autoFocus?: boolean;
  'data-testid'?: string;
  disabled?: boolean;
  expand?: boolean;
  field: ReduxFormField<T>;
  filter?: InputFilter;
  id?: string;
  maxLength?: number;
  modifierClassName?: string;
  formNoValidate?: boolean;
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
  onFocus?(event: React.FocusEvent<HTMLInputElement>): void;
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void;
  onKeyPress?(event: React.KeyboardEvent<HTMLInputElement>): void;
  pattern?: string;
  placeholder?: string;
  postfix?: JSX.Element | string;
  prefix?: JSX.Element | string;
  required?: boolean;
  'aria-controls'?: string;
  'aria-expanded'?: boolean;
  'aria-activedescendant'?: string;
  role?: string;
  tabIndex?: number;
  readOnly?: boolean;
  type?: string;
}

interface State {
  focused: boolean;
}

/**
 * @deprecated
 * Please see the inputs in the ui folder of your project
 */
export default class TextInput<T = string | number> extends BaseComponent<Props<T>, State> {
  static defaultProps = {
    autoFocus: false,
    type: 'text',
    modifierClassName: 'TextInput--default',
  };

  state: State = {
    focused: false,
  };

  inputNode = React.createRef<Input<T>>();

  focus() {
    if (!this.inputNode.current || !this.inputNode.current.input.current) {
      return;
    }
    this.inputNode.current.input.current.focus();
  }

  select() {
    if (!this.inputNode.current || !this.inputNode.current.input.current) {
      return;
    }
    this.inputNode.current.input.current.select();
  }

  blur() {
    if (!this.inputNode.current || !this.inputNode.current.input.current) {
      return;
    }
    this.inputNode.current.input.current.blur();
  }

  handleFocus(evt: React.FocusEvent<HTMLInputElement>) {
    this.setState((prevState: State) => ({
      focused: true,
    }));
    if (this.props.onFocus) {
      this.props.onFocus(evt);
    }
  }

  handleBlur(evt: React.FocusEvent<HTMLInputElement>) {
    this.setState((prevState: State) => ({
      focused: false,
    }));
    if (this.props.onBlur) {
      this.props.onBlur(evt);
    }
  }

  render() {
    const { modifierClassName, postfix, prefix, tabIndex, type, field } = this.props;

    const inputProps = _.omit(this.props, ['onBlur', 'onFocus', 'field']);

    const hasError = field && field.touched && field.error;

    const className = clsx('TextInput', modifierClassName, {
      'has-prefix': !!prefix,
      'has-postfix': !!postfix,
      'TextInput--is-focused': this.state.focused,
      'TextInput--is-error': hasError,
    });
    const inputClassName = clsx('TextInput-input', {
      'has-prefix': !!prefix,
      'has-postfix': !!postfix,
    });

    let decoratedPrefix: JSX.Element | undefined;
    let decoratedPostfix: JSX.Element | undefined;

    if (prefix) {
      if (typeof prefix === 'string') {
        decoratedPrefix = (
          <TextInputAffix text={prefix} className="TextInput-prefix" key="TextInput-prefix" />
        );
      } else {
        const prefixClassName: string = (oc(prefix) as any).props.className('');
        decoratedPrefix = React.cloneElement(prefix, {
          className: clsx('TextInput-prefix', prefixClassName),
        });
      }
    }

    if (postfix) {
      if (typeof postfix === 'string') {
        decoratedPostfix = (
          <TextInputAffix text={postfix} className="TextInput-postfix" key="TextInput-postfix" />
        );
      } else {
        const postfixClassName: string = (oc(postfix) as any).props.className('');
        decoratedPostfix = React.cloneElement(postfix, {
          className: clsx('TextInput-postfix', postfixClassName),
        });
      }
    }

    return (
      <InputContainer className={className}>
        {decoratedPrefix}

        <Input
          data-testid={this.props['data-testid']}
          ref={this.inputNode}
          classNames={inputClassName}
          field={field}
          key="TextInput"
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          tabIndex={tabIndex}
          type={type}
          {...inputProps}
        />

        {decoratedPostfix}
      </InputContainer>
    );
  }
}
